"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const DISMISS_STORAGE_KEY = "pwa-install-dismissed";
const DISMISS_COOLDOWN_DAYS = 7;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: "accepted" | "dismissed" }>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

function wasDismissedRecently(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (Number.isNaN(ts)) return false;
    const cutoff = Date.now() - DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    return ts > cutoff;
  } catch {
    return false;
  }
}

function setDismissed(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function PwaInstallBanner() {
  const [showInstall, setShowInstall] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const installPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  const dismiss = useCallback(() => {
    setDismissed();
    setShowInstall(false);
    setShowIosInstructions(false);
  }, []);

  useEffect(() => {
    if (!isMobileDevice() || isStandalone() || wasDismissedRecently()) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      installPromptRef.current = e as BeforeInstallPromptEvent;
      setShowInstall(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    if (isIos() && !isStandalone()) {
      setShowIosInstructions(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = useCallback(() => {
    const ev = installPromptRef.current;
    if (!ev) return;
    ev.prompt().then((choice) => {
      if (choice.outcome === "accepted") dismiss();
      setShowInstall(false);
      installPromptRef.current = null;
    });
  }, [dismiss]);

  const visible = showInstall || showIosInstructions;
  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] border-t border-border bg-card shadow-lg"
      role="dialog"
      aria-label="Add to home screen"
    >
      <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {showInstall ? (
            "Install Book4 on your device for quick access and a better experience."
          ) : (
            <>
              Add Book4 to your home screen: tap the <strong>Share</strong>{" "}
              button, then <strong>Add to Home Screen</strong>.
            </>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {showInstall && (
            <button
              type="button"
              onClick={handleInstallClick}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Install app
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
            aria-label="Dismiss"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
